import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const NotifCtx = createContext(null);

const POLL_MS = 30000;

export function NotificationsProvider({ children }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);
    const lastToastIdsRef = useRef(new Set());

    const refresh = useCallback(async (opts = { silent: true, showNewToast: false }) => {
        if (!user) return;
        if (!opts.silent) setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 30 } });
            const list = data.notifications || [];
            setUnread(data.unread || 0);

            if (opts.showNewToast) {
                // Toast for any new unread notifs we haven't shown yet
                const knownIds = lastToastIdsRef.current;
                list.filter((n) => !n.read && !knownIds.has(n.id)).forEach((n) => {
                    knownIds.add(n.id);
                    toast(n.title, { description: n.body });
                });
                // Cap set size
                if (knownIds.size > 100) {
                    lastToastIdsRef.current = new Set(Array.from(knownIds).slice(-50));
                }
            } else {
                // Seed known ids so we don't spam on first mount
                list.forEach((n) => lastToastIdsRef.current.add(n.id));
            }
            setItems(list);
        } catch (e) {
            // silent
        } finally {
            if (!opts.silent) setLoading(false);
        }
    }, [user]);

    const markRead = useCallback(async (id) => {
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        setUnread((u) => Math.max(0, u - 1));
        try {
            await api.post(`/notifications/${id}/read`);
        } catch (e) {}
    }, []);

    const markAllRead = useCallback(async () => {
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnread(0);
        try {
            await api.post("/notifications/read-all");
        } catch (e) {}
    }, []);

    useEffect(() => {
        if (!user) {
            setItems([]);
            setUnread(0);
            lastToastIdsRef.current = new Set();
            if (pollRef.current) clearInterval(pollRef.current);
            return;
        }
        // Initial fetch (no toast for existing notifs)
        refresh({ silent: true, showNewToast: false });
        pollRef.current = setInterval(() => {
            refresh({ silent: true, showNewToast: true });
        }, POLL_MS);
        return () => pollRef.current && clearInterval(pollRef.current);
    }, [user, refresh]);

    const value = { items, unread, loading, refresh, markRead, markAllRead };
    return <NotifCtx.Provider value={value}>{children}</NotifCtx.Provider>;
}

export function useNotifications() {
    const ctx = useContext(NotifCtx);
    if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
    return ctx;
}
